/**
 * Environment detection and configuration
 * Automatically detects dev vs prod based on URL
 */

export type Environment = 'dev' | 'staging' | 'prod';

export interface EnvironmentConfig {
  environment: Environment;
  apiUrl: string;
  adminUrl: string;
  chatUrl: string;
  ticketsUrl: string;
  isDevelopment: boolean;
  isProduction: boolean;
}

/**
 * Detect environment based on hostname
 * Dev: *-dev.coresupportai.com or localhost
 * Staging: *-staging.coresupportai.com
 * Prod: *.coresupportai.com or *.coresupport.ai
 */
export const detectEnvironment = (): Environment => {
  const hostname = window.location.hostname;
  
  // Development environment indicators
  if (
    hostname.includes('-dev.') ||
    hostname.includes('localhost') ||
    hostname.includes('127.0.0.1')
  ) {
    return 'dev';
  }
  
  // Staging environment
  if (hostname.includes('-staging.')) {
    return 'staging';
  }
  
  // Production environment (default)
  return 'prod';
};

/**
 * Get API URL based on environment
 */
export const getApiUrl = (): string => {
  const env = detectEnvironment();
  
  // Check for override via environment variable (build time)
  // Note: import.meta.env is only available in Vite projects
  // For Create React App, use process.env.REACT_APP_API_URL instead
  
  // Runtime detection based on URL
  if (env === 'dev') {
    return 'https://5hx02st2hl.execute-api.us-east-1.amazonaws.com/dev';
  }
  
  if (env === 'staging') {
    return 'https://api-staging.coresupportai.com';
  }
  
  return 'https://api.coresupportai.com';
};

/**
 * Get complete environment configuration
 */
export const getConfig = (): EnvironmentConfig => {
  const env = detectEnvironment();
  const isDev = env === 'dev';
  
  // API URLs by environment
  const apiUrls = {
    dev: 'https://5hx02st2hl.execute-api.us-east-1.amazonaws.com/dev',
    staging: 'https://api-staging.coresupportai.com',
    prod: 'https://api.coresupportai.com'
  };
  
  const adminUrls = {
    dev: 'https://admin-dev.coresupportai.com',
    staging: 'https://admin-staging.coresupportai.com',
    prod: 'https://admin.coresupportai.com'
  };
  
  const chatUrls = {
    dev: 'https://app-dev.coresupportai.com',
    staging: 'https://app-staging.coresupportai.com',
    prod: 'https://app.coresupportai.com'
  };
  
  const ticketsUrls = {
    dev: 'https://tickets-dev.coresupportai.com',
    staging: 'https://tickets-staging.coresupportai.com',
    prod: 'https://tickets.coresupportai.com'
  };
  
  return {
    environment: env,
    isDevelopment: isDev,
    isProduction: !isDev,
    apiUrl: apiUrls[env],
    adminUrl: adminUrls[env],
    chatUrl: chatUrls[env],
    ticketsUrl: ticketsUrls[env],
  };
};

/**
 * Log environment information (useful for debugging)
 */
export const logEnvironmentInfo = (): void => {
  const config = getConfig();
  console.log('🌍 Environment Configuration:', {
    environment: config.environment,
    hostname: window.location.hostname,
    apiUrl: config.apiUrl,
    isDevelopment: config.isDevelopment,
  });
};

// Export singleton config
export const config = getConfig();

// Log on module load (only in development)
if (config.isDevelopment) {
  logEnvironmentInfo();
}
